import { API_BASE_URL } from "@/api/helpers";
import { log } from "@/core/logger";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";

export type BookingReassignedPayload = {
  bookingId: string;
  locationId: string;
  oldMemberId: string;
  newMemberId: string;
};

/**
 * Listens on a dedicated Socket.IO connection for server-pushed booking mutation events
 * and invalidates the relevant React Query caches so views (calendar, booking detail,
 * Your Bookings, counts) reflect the change without requiring a page refresh.
 *
 * Mount once per authenticated session — typically inside the auth-wrapped layout. The
 * server auto-subscribes the socket to a `location:<id>` room for each location the
 * member can see, so a reassignment performed by an admin on a different browser still
 * reaches every other member viewing that location.
 */
type UseBookingSocketListenerOptions = {
  /**
   * Called when the server pushes a `member_updated` event to this member's socket (e.g.
   * after a role/permission change). Wired to a live member refresh so the change reflects
   * without a logout/login.
   */
  onMemberUpdated?: () => void;
};

export const useBookingSocketListener = (enabled: boolean, options?: UseBookingSocketListenerOptions) => {
  const queryClient = useQueryClient();
  // Keep the latest callback in a ref so a changing callback identity doesn't tear down and
  // recreate the socket connection on every render.
  const onMemberUpdatedRef = useRef(options?.onMemberUpdated);
  useEffect(() => {
    onMemberUpdatedRef.current = options?.onMemberUpdated;
  }, [options?.onMemberUpdated]);

  useEffect(() => {
    if (!enabled) return;

    const socket: Socket = io(API_BASE_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socket.on("booking_reassigned", (payload: BookingReassignedPayload) => {
      log("useBookingSocketListener", "Received booking_reassigned event", payload);
      // Invalidate every cache that may show the booking under its old member. This
      // mirrors useAssignBookingToMember.onSuccess but runs on receivers across the
      // location — including users who never made the change themselves.
      queryClient.invalidateQueries({ queryKey: ["booking"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["bookings"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["member-bookings"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["booking-counts"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["booking-counts-by-location"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["member", payload.oldMemberId], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["member", payload.newMemberId], refetchType: "all" });
    });

    socket.on("member_updated", (payload: { memberId: string }) => {
      log("useBookingSocketListener", "Received member_updated event", payload);
      // Invalidate any cached member-scoped data, then trigger the live member refresh so the
      // new role/permissions (and the menus they gate) reflect without a logout/login.
      queryClient.invalidateQueries({ queryKey: ["members"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["member", payload.memberId], refetchType: "all" });
      onMemberUpdatedRef.current?.();
    });

    socket.on("connect_error", (error) => {
      log("useBookingSocketListener", "WebSocket connection error (will retry):", error.message);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [enabled, queryClient]);
};
